export type FeatureStatus = "shipped" | "later";

export type FeatureSurface =
  | "Home"
  | "Science"
  | "Explorer"
  | "Site";

export interface GlossaryReference {
  label: string;
  url: string;
}

export interface GlossaryTerm {
  id: string;
  term: string;
  meaning: string;
  /** Optional papers or agency notes shown under the definition. */
  references?: GlossaryReference[];
}

export interface GlossaryGroup {
  id: string;
  title: string;
  tag: string;
  summary: string;
  usedOn: { label: string; to: string }[];
  termIds: string[];
}

export interface SiteFeature {
  id: string;
  title: string;
  surface: FeatureSurface;
  status: FeatureStatus;
  summary: string;
}

/**
 * Labels shown on the Science glossary, plus an internal feature list
 * (SITE_FEATURES) that is not rendered on the public site.
 */
export const GLOSSARY: GlossaryTerm[] = [
  {
    id: "blocking",
    term: "Atmospheric blocking",
    meaning:
      "A persistent buckle in the jet stream that parks weather over the same region for days or weeks — heat, drought, flood, or gloom.",
    references: [
      {
        label: "Woollings et al. (2018) · blocking review",
        url: "https://link.springer.com/article/10.1007/s40641-018-0108-z",
      },
      {
        label: "New Scientist · Cuff 2025",
        url: "https://www.newscientist.com/article/2463818-is-a-broken-jet-stream-causing-extreme-weather-that-lasts-longer/",
      },
    ],
  },
  {
    id: "z500",
    term: "Z500",
    meaning:
      "Geopotential height at 500 hPa (about 5.5 km up), in geopotential metres. High values are ridges; low values are troughs. Blocking shows up as a stubborn ridge. This is a map layer.",
  },
  {
    id: "z500-star",
    term: "Z500*",
    meaning:
      "Zonal anomaly of Z500: at each latitude the west–east mean height is subtracted. Units are geopotential metres. Positive = higher than the zonal mean (ridge). This is a map layer, not the model heatmap.",
  },
  {
    id: "era5",
    term: "ERA5 / ERA5T",
    meaning:
      "ECMWF reanalysis. ERA5 is the delayed, quality-controlled record used in the thesis. ERA5T is the near-real-time update used for 2026 maps.",
  },
  {
    id: "t2m-daily-max",
    term: "Daily maximum T2m",
    meaning:
      "The highest 2 m air temperature sampled during the UTC day, shown in °C as weather context. Historical maps use 6-hourly WeatherBench2 ERA5 samples; 2026 maps use hourly ARCO ERA5T samples. Temperature was not an input to the CNN.",
  },
  {
    id: "jja",
    term: "JJA",
    meaning:
      "June–July–August. The CNN was trained and evaluated on European summer only.",
  },
  {
    id: "omega-block",
    term: "Omega block",
    meaning:
      "A blocked high locked between lows on either side, so the upper-level flow bends into an Ω shape. Commonly linked to trapped heat and diverted storm tracks.",
    references: [
      {
        label: "NOAA Climate.gov · 2015 European omega block",
        url: "https://www.climate.gov/news-features/event-tracker/summer-heat-wave-arrives-europe",
      },
    ],
  },
  {
    id: "stationary-ridge",
    term: "Stationary ridge",
    meaning:
      "A persistent open high-amplitude ridge that stays nearly fixed in place. It is a locally contentious form of blocking — related to, but not identical with, a closed omega block.",
    references: [
      {
        label: "Woollings et al. (2018) · stationary-ridge classification",
        url: "https://link.springer.com/article/10.1007/s40641-018-0108-z",
      },
    ],
  },
  {
    id: "high-pressure-ridge",
    term: "High-pressure ridge",
    meaning:
      "An elongated tongue of high geopotential height (or surface pressure) that can steer warm, dry air into a region without necessarily forming a closed omega block.",
    references: [
      {
        label: "Struzewska & Kaminski (2008) · Eastern European ridge",
        url: "https://acp.copernicus.org/articles/8/721/2008/",
      },
    ],
  },
  {
    id: "double-jet",
    term: "Double-jet regime",
    meaning:
      "A split upper-level jet with two distinct westerly branches. It is a background flow regime, not a local block morphology — a heatwave can sit under a ridge while a longer Eurasian double-jet spell is underway.",
    references: [
      {
        label: "Rousi, Kornhuber, Coumou et al. (2022) · Eurasian double jets",
        url: "https://www.nature.com/articles/s41467-022-31432-y",
      },
    ],
  },
  {
    id: "gtd",
    term: "GTD",
    meaning:
      "Ground Truth Dataset: days labelled blocked after combining TM90, DG83, and S04 with expert review. The published F1 0.72 / ~78% recall describe detection against this set for 1979–2018.",
    references: [
      {
        label: "Thomas et al. (2021) · expert GTD and index disagreement",
        url: "https://wcd.copernicus.org/articles/2/581/2021/",
      },
    ],
  },
  {
    id: "tm90",
    term: "TM90",
    meaning:
      "Tibaldi–Molteni 1990 blocking index. Uses the north–south Z500 gradient to find a reversal of the usual westerly flow.",
    references: [
      {
        label: "Tibaldi & Molteni (1990)",
        url: "https://doi.org/10.3402/tellusa.v42i3.11881",
      },
    ],
  },
  {
    id: "dg83",
    term: "DG83",
    meaning:
      "Dole–Gordon 1983 blocking index. Uses a persistent Z500 anomaly (height much higher than usual) rather than a gradient reversal.",
    references: [
      {
        label: "Dole & Gordon (1983)",
        url: "https://doi.org/10.1175/1520-0493(1983)111%3C1567:POTNAA%3E2.0.CO;2",
      },
    ],
  },
  {
    id: "s04",
    term: "S04",
    meaning:
      "Schwierz et al. 2004 blocking index. Uses a potential-vorticity (PV) anomaly — a dynamical marker of a blocked air mass.",
    references: [
      {
        label: "Schwierz et al. (2004)",
        url: "https://doi.org/10.1175/1520-0493(2004)132%3C1849:FDIB%3E2.0.CO;2",
      },
    ],
  },
  {
    id: "cnn",
    term: "CNN / Blocking_Net",
    meaning:
      "The thesis convolutional network. Input is a 7-day stack of Z500* on the Europe grid. Output is P(blocked) for the middle day, plus a Grad-CAM heatmap.",
  },
  {
    id: "window",
    term: "7-day window",
    meaning:
      "Each prediction uses ±3 days around the labelled day. The map you see is that middle day at 00:00 UTC.",
  },
  {
    id: "p-blocked",
    term: "CNN P(blocked)",
    meaning:
      "Domain-wide probability that the middle day is blocked, from the CNN softmax. The large number on the side card is this day; Peak CNN P(blocked) is the highest day in the event window.",
  },
  {
    id: "gradcam",
    term: "Grad-CAM",
    meaning:
      "Gradient-weighted Class Activation Mapping. It highlights the grid cells whose patterns most influenced the CNN’s “blocked” decision. Values 0–1. This is model attention, not a second weather field.",
  },
  {
    id: "heatmap",
    term: "Model heatmap",
    meaning:
      "The Grad-CAM field on the map. Toggle it against Z500* to see the atmosphere versus where the CNN looked.",
  },
  {
    id: "onset",
    term: "Onset",
    meaning:
      "Early in the event window — the block is forming. Assigned from where the peak CNN probability sits in the window, not from a separate onset model.",
  },
  {
    id: "mature",
    term: "Mature",
    meaning:
      "The block is established. Peak probability sits in the middle of the window.",
  },
  {
    id: "decay",
    term: "Decay",
    meaning:
      "Late in the event window — the block is breaking down.",
  },
  {
    id: "unknown-stage",
    term: "Unknown (stage)",
    meaning:
      "The window is too short, or there is no clear onset/mature/decay split (for example the quiet contrast days).",
  },
  {
    id: "cnn-only",
    term: "CNN-only",
    meaning:
      "The CNN flagged a blocking-like pattern that GTD / TM90 / DG83 / S04 did not. Candidate missed events requiring further study, not expert-confirmed blocks.",
    references: [
      {
        label: "Thomas et al. (2021) · expert GTD and index disagreement",
        url: "https://wcd.copernicus.org/articles/2/581/2021/",
      },
    ],
  },
  {
    id: "validated-thesis",
    term: "Validated thesis · 1979–2018",
    meaning:
      "Dates from the thesis ERA5 record. GTD labels exist. The published metrics describe this era. This badge is not a train/test split — some of these days were used to train or validate the weights; that year mask is not stored yet.",
  },
  {
    id: "new-2026",
    term: "New 2026 · never used in training",
    meaning:
      "ERA5T dates after training finished (2020). Same weights, new atmosphere, no GTD labels yet. The only explorer events that are clearly post-training.",
  },
  {
    id: "index-average",
    term: "Index average",
    meaning:
      "Share of TM90 / DG83 / S04 that flagged a block, when all three labels are known. Shown as a percentage. Not computed for 2026 or when a vote is missing.",
  },
  {
    id: "peak-day",
    term: "Peak day",
    meaning:
      "The day in the event window with the highest CNN P(blocked). Marked on the map and in the calendar. Colour scales stay fixed for the whole event so that day is comparable to its neighbours.",
  },
  {
    id: "f1",
    term: "F1 / recall",
    meaning:
      "Thesis detection scores against GTD on held-out test years (last 10 of 1979–2018): F1 0.72, ~78% recall. They do not describe 2026, and they do not mean every historical map in the explorer was unseen during training.",
  },
];

export const GLOSSARY_GROUPS: GlossaryGroup[] = [
  {
    id: "atmosphere",
    title: "Atmosphere & fields",
    tag: "Atmosphere",
    summary: "What blocking is, and the height fields on the map.",
    usedOn: [
      { label: "Explore map", to: "/explore" },
      { label: "Science", to: "/science" },
    ],
    termIds: ["blocking", "z500", "z500-star", "t2m-daily-max", "era5", "jja"],
  },
  {
    id: "patterns",
    title: "Circulation patterns",
    tag: "Patterns",
    summary:
      "Named morphologies and background flow regimes shown on Explore event badges.",
    usedOn: [
      { label: "Explore events", to: "/explore" },
      { label: "Science", to: "/science" },
    ],
    termIds: [
      "omega-block",
      "stationary-ridge",
      "high-pressure-ridge",
      "double-jet",
    ],
  },
  {
    id: "indices",
    title: "Indices & ground truth",
    tag: "Indices",
    summary: "How a day is labelled blocked — GTD and the three classical indices.",
    usedOn: [{ label: "Explore side card", to: "/explore" }],
    termIds: ["gtd", "tm90", "dg83", "s04", "index-average", "cnn-only"],
  },
  {
    id: "model",
    title: "CNN & scores",
    tag: "CNN",
    summary: "The network, its 7-day window, Grad-CAM, and thesis metrics.",
    usedOn: [
      { label: "Explore map", to: "/explore" },
      { label: "Science", to: "/science" },
    ],
    termIds: ["cnn", "window", "p-blocked", "gradcam", "heatmap", "f1"],
  },
  {
    id: "stages",
    title: "Event stage",
    tag: "Stages",
    summary: "Where the peak sits in the window — forming, established, or breaking down.",
    usedOn: [{ label: "Explore events", to: "/explore" }],
    termIds: ["onset", "mature", "decay", "unknown-stage", "peak-day"],
  },
  {
    id: "scopes",
    title: "Date scopes",
    tag: "Scopes",
    summary: "Thesis-era ERA5 versus post-training 2026. Not a train/test split.",
    usedOn: [{ label: "Explore badges", to: "/explore" }],
    termIds: ["validated-thesis", "new-2026"],
  },
];

export function glossaryHref(id: string): string {
  return `/science#term-${id}`;
}

export function glossaryGroupHref(groupId: string): string {
  return `/science#glossary-${groupId}`;
}

/** Map an Explore event stage pill to its glossary term id. */
export function stageGlossaryId(stage: string): string {
  if (stage === "unknown") return "unknown-stage";
  return stage;
}

/**
 * Map a circulation-pattern label to a glossary term when the wording is known.
 * Returns null for unresolved / out-of-season phrases without a dedicated entry.
 */
export function patternGlossaryId(label: string): string | null {
  const key = label.toLowerCase();
  if (key.includes("omega")) return "omega-block";
  if (key.includes("stationary ridge")) return "stationary-ridge";
  if (key.includes("high-pressure ridge") || key.includes("high pressure ridge")) {
    return "high-pressure-ridge";
  }
  if (key.includes("double-jet") || key.includes("double jet")) {
    return "double-jet";
  }
  return null;
}

export function glossaryTermsInGroup(group: GlossaryGroup): GlossaryTerm[] {
  return group.termIds
    .map((id) => GLOSSARY.find((item) => item.id === id))
    .filter((item): item is GlossaryTerm => Boolean(item));
}

export function glossaryGroupForTerm(termId: string): GlossaryGroup | undefined {
  return GLOSSARY_GROUPS.find((group) => group.termIds.includes(termId));
}

export function glossaryGroupFromHash(hash: string): string | null {
  const value = hash.replace(/^#/, "");
  if (value.startsWith("glossary-")) {
    const id = value.slice("glossary-".length);
    return GLOSSARY_GROUPS.some((group) => group.id === id) ? id : null;
  }
  if (value.startsWith("term-")) {
    return glossaryGroupForTerm(value.slice("term-".length))?.id ?? null;
  }
  return null;
}

/** Compact explorer captions. Long definitions live in GLOSSARY — do not restate them on /explore. */
export interface ExplorerLayerNote {
  glossaryId: string;
  label: string;
  short: string;
}

export const EXPLORER_LAYER_NOTES: ExplorerLayerNote[] = [
  {
    glossaryId: "z500",
    label: "Z500",
    short: "Absolute 500 hPa height — ridges high, troughs low (gpm).",
  },
  {
    glossaryId: "z500-star",
    label: "Z500*",
    short: "Mid-level height anomaly versus the west–east mean (gpm).",
  },
  {
    glossaryId: "gradcam",
    label: "Grad-CAM",
    short: "Where the CNN looked when it decided “blocked”.",
  },
  {
    glossaryId: "p-blocked",
    label: "P(blocked)",
    short: "Domain probability that this 00:00 UTC day is blocked.",
  },
];

export const EXPLORER_META_CHIPS = [
  "ERA5 · 1979–2018",
  "ERA5T · 2026",
  "00:00 UTC",
  "1° Europe grid",
] as const;

export const SITE_FEATURES: SiteFeature[] = [
  {
    id: "home-pitch",
    title: "Pitch home",
    surface: "Home",
    status: "shipped",
    summary:
      "Hero with Explore and Understand the science CTAs, omega-block diagram, F1 / recall / index-disagreement stats, product direction, proven–validating–to-prove pipeline. Sign-up lives in the navbar.",
  },
  {
    id: "science-page",
    title: "Science page",
    surface: "Science",
    status: "shipped",
    summary:
      "Blocking explainer, thesis metrics, geopotential-height note, proven vs not-yet-proven, thesis PDF, Explore CTA to the maps.",
  },
  {
    id: "blog",
    title: "Reviews",
    surface: "Site",
    status: "shipped",
    summary:
      "Markdown reviews at /reviews and /reviews/<slug>. /blog redirects there. Linked from the nav and footer, with BlogPosting metadata and sitemap entries.",
  },
  {
    id: "glossary",
    title: "Labels & terminology",
    surface: "Science",
    status: "shipped",
    summary:
      "Grouped, searchable glossary of explorer and thesis terms.",
  },
  {
    id: "feature-catalog",
    title: "Shipped / later feature lists",
    surface: "Science",
    status: "shipped",
    summary:
      "Internal catalog in siteCatalog.ts for agents. Not rendered on the public site.",
  },
  {
    id: "theme",
    title: "Light / dark theme",
    surface: "Site",
    status: "shipped",
    summary: "Navbar toggle; maps and chrome follow the theme.",
  },
  {
    id: "explorer-events",
    title: "Event catalogue",
    surface: "Explorer",
    status: "shipped",
    summary:
      "Filterable event tiles (scope, hazard, search), regime-event cards with peak P(blocked), sources, stage pills, CNN-only badge.",
  },
  {
    id: "explorer-map",
    title: "Blocking map",
    surface: "Explorer",
    status: "shipped",
    summary:
      "Orientation notes, Z500 / Z500* / Grad-CAM toggles, concise daily averages, unified point tooltip with contextual Tmax, event-fixed colour scale.",
  },
  {
    id: "explorer-playback",
    title: "Date playback",
    surface: "Explorer",
    status: "shipped",
    summary:
      "Play / pause / prev / next / start / end, calendar of mapped days, no viewport jump while playing.",
  },
  {
    id: "explorer-side-card",
    title: "Day side card",
    surface: "Explorer",
    status: "shipped",
    summary:
      "CNN P(blocked), peak in window, event-details link, GTD / TM90 / DG83 / S04 calls, and index average with missing-data help.",
  },
  {
    id: "dropdown",
    title: "Branded dropdown",
    surface: "Site",
    status: "shipped",
    summary:
      "Accessible listbox used for scope, event type, and colour map; covered by tests.",
  },
  {
    id: "static-export",
    title: "Offline CNN export",
    surface: "Explorer",
    status: "shipped",
    summary:
      "generate_export.py writes daily map JSON (Z500* + heatmap + P(blocked)) for the selected event windows.",
  },
  {
    id: "split-tags",
    title: "Train / test / post-thesis date tags",
    surface: "Explorer",
    status: "later",
    summary:
      "Do not ship until the epoch-19 year mask is confirmed. Then tag each date as train, held-out test, or post-thesis so in-sample maps are not read as unseen predictions.",
  },
  {
    id: "more-events",
    title: "Fuller event archive",
    surface: "Explorer",
    status: "later",
    summary:
      "More GTD / CNN-only windows (and optionally every JJA day). ~95 KB per day; blocked-day-only 2000–2026 is tens of MB, every summer day ~230 MB.",
  },
  {
    id: "per-day-indices",
    title: "Per-day index labels",
    surface: "Explorer",
    status: "later",
    summary:
      "TM90 / DG83 / S04 / GTD currently inherit from the event, not the specific day. Wire daily labels when they exist.",
  },
  {
    id: "permalinks",
    title: "Shareable event / day URLs",
    surface: "Explorer",
    status: "later",
    summary: "Deep-link to a play group and date so a pitch conversation can land on the same map.",
  },
  {
    id: "compact-maps",
    title: "Smaller map payloads",
    surface: "Explorer",
    status: "later",
    summary:
      "Quantize or compress the 46×61 grids if the archive grows beyond a handful of events.",
  },
  {
    id: "forecast",
    title: "Forecast / lead-time maps",
    surface: "Explorer",
    status: "later",
    summary:
      "Not proven. Detection on reanalysis is not a t+7 or t+14 outlook. Keep this off the current explorer until a held-out forecast test exists.",
  },
];

export const shippedFeatures = () =>
  SITE_FEATURES.filter((feature) => feature.status === "shipped");

export const laterFeatures = () =>
  SITE_FEATURES.filter((feature) => feature.status === "later");
